import { Check } from '@mui/icons-material';
import { type PublicMeasurementResponseRecord } from '@on3/api';
import clsx from 'clsx';
import { useCallback } from 'react';

import styles from './VitalsMeasurement.module.scss';

interface MeasurementProps {
  data: PublicMeasurementResponseRecord;
}

export const Measurement = ({ data }: MeasurementProps) => {
  const hasValue = data.value && data.value !== 0;

  const formatValue = useCallback(
    (value?: number | null) => {
      if (!value && value !== 0) return '-';

      // Height
      if (data.measurementTypeKey === 1) {
        return `${Math.floor(value / 12)}-${(value % 12).toFixed(0)}`;
      }

      // Weight
      if (data.measurementTypeKey === 2) return Math.round(value);

      // 40 Yard Dash
      if (data.measurementTypeKey === 6) {
        return value.toFixed(2);
      }

      return value.toFixed(1);
    },
    [data.measurementTypeKey],
  );

  const formatAverage = (average?: number | null) => {
    if (!average) return '-';

    return average.toFixed(1);
  };

  const formatHeight = useCallback(
    (value?: number | null, fallback: string = '50px') => {
      if (!value) return { height: fallback };

      const topValue = Math.max(
        data.value ?? 0,
        data.top300Average ?? 0,
        data.draftedAverage ?? 0,
      );

      return { height: `calc(${(value ?? 0) / topValue} * 150px)` };
    },
    [data],
  );

  return (
    <div
      aria-labelledby={`graph-${data.type?.toLowerCase()}`}
      className={styles.block}
    >
      <h3 id={`graph-${data.type?.toLowerCase()}`}>{data.type}</h3>
      <div className={styles.verified}>
        {data.verified && (
          <>
            <figure>
              <Check />
            </figure>
            <span>On3 Verified</span>
          </>
        )}
      </div>
      <div
        className={clsx(styles.graph, {
          [styles.empty]: !hasValue,
        })}
      >
        <div
          className={styles.bar}
          data-player
          style={formatHeight(data.value, '60px')}
        >
          <span>{formatValue(data.value)}</span>
        </div>
        <div
          className={styles.bar}
          data-top300
          style={formatHeight(data.top300Average, '80px')}
        >
          <span>{formatValue(data.top300Average)}</span>
        </div>
        <div
          className={styles.bar}
          data-drafted
          style={formatHeight(data.draftedAverage, '100px')}
        >
          <span>{formatValue(data.draftedAverage)}</span>
        </div>
      </div>
      {hasValue && (
        <div className={styles.averages}>
          <span
            className={clsx({
              [styles.positive]: (data.topAverageChangePercent ?? 0) >= 100,
              [styles.negative]: (data.topAverageChangePercent ?? 0) < 100,
            })}
          >
            {`${formatAverage(data.topAverageChangePercent)}%`}
          </span>
          <span
            className={clsx({
              [styles.positive]: (data.draftChangePercent ?? 0) >= 100,
              [styles.negative]: (data.draftChangePercent ?? 100) < 100,
            })}
          >
            {`${formatAverage(data.draftChangePercent)}%`}
          </span>
        </div>
      )}
    </div>
  );
};
